import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle
} from 'react';
import { Col, Row } from 'react-bootstrap';
import { cooperplaceApi } from 'services/api';
import {
  fetchAnttTypes,
  fetchBodies,
  fetchOnlyCities,
  fetchImplements,
  fetchOwners,
  fetchVehiclesTypes,
} from 'utils/fetches';

import { Input, Select, Text } from 'v3/components';
import { useSnackbar } from 'v3/components/Snackbar';
import BranchOfficesSelect, { toBranchOfficeIds } from 'v3/components/BranchOfficesSelect';
import { wagonRegistrationSchema } from '../../validator';

const ManualRegisterWagon = forwardRef(({
  data,
  setData,
  errors,
  setErrors,
  setLoading,
  handleClose,
  wagonKey,
  sendResponse
}, ref) => {

  const [vehicleTypesOptions, setVehicleTypesOptions] = useState(null);
  const [vehicleBodyTypesOptions, setVehicleBodyTypesOptions] = useState(null);
  const [vehicleImplementTypesOptions, setVehicleImplementTypesOptions] = useState(null);
  const [anttTypesOptions, setAnttTypesOptions] = useState(null);

  const snackbar = useSnackbar();

  const hasSelectDollyImplementType = data.vehicleImplementType?.name === 'Dolly';

  const handleChange = (field, value) => {
    setData(old => ({
      ...old,
      [field]: value,
    }));
  };

  async function fetchOptions() {
    try {

      let [
        vehicleTypesData,
        vehiclesBodyTypesData,
        anttTypesData,
        implementsData,
      ] = await Promise.all([
        fetchVehiclesTypes(),
        fetchBodies(),
        fetchAnttTypes(),
        fetchImplements(),
      ]);
      vehicleTypesData = vehicleTypesData.filter(
        vtype => vtype?.name?.toLowerCase() === 'semi reboque'
      );
      setVehicleTypesOptions(vehicleTypesData);
      setVehicleBodyTypesOptions(vehiclesBodyTypesData);
      setAnttTypesOptions(anttTypesData);
      setVehicleImplementTypesOptions(implementsData);
    } catch (error) {
      //
    }
  }

  useEffect(() => {
    fetchOptions();
  }, []);

  async function verifySchema(payload) {
    try {
      await wagonRegistrationSchema.validate(payload, {
        abortEarly: false,
      });
      return true;
    } catch (error) {
      const errorResponse = {};
      if (error.inner) {
        error.inner.forEach(error => {
          errorResponse[error.path] = error.message;
        });
        setErrors(errorResponse);
      }
      return false;
    }
  }

  async function handleSubmit() {
    try {
      setLoading(true);
      let payload = {
        plate: data.plate,
        yearManufacture: data.yearManufacture,
        chassi: data.chassi,
        renavam: data.renavam,
        yearModel: data.yearModel,
        vehicleType: data.vehicleType,
        vehicleImplementType: data.vehicleImplementType,
        vehicleImplementTypeName: data.vehicleImplementType?.name,
        vehicleBodyType: data.vehicleBodyType,
        anttType: data.anttType,
        antt: data.antt,
        anttAdherence: data.anttAdherence,
        idOwner: data.ownerData?.id,
        plateCityId: data.city?.id,
        beneficiaryId: data.beneficiary?.id,
        branchOffices: data.branchOffices,
      };
      const isValid = await verifySchema(payload);
      if (isValid === false) {
        setLoading(false);
        return;
      }
      const { branchOffices, ...wagonPayload } = payload;
      payload = {
        ...wagonPayload,
        branchOfficeIds: toBranchOfficeIds(branchOffices),
        plateState: data.city?.province?.uf,
        plateCity: data.city?.name,
        anttType: data.anttType?.id,
        vehicleTypeId: data.vehicleType?.id,
        vehicleBodyTypeId: data.vehicleBodyType?.id,
        vehicleImplementTypeId: data.vehicleImplementType?.id,
      };

      const response = await cooperplaceApi.post('wagons', payload);
      snackbar.show(<Text>Cadastro realizado com sucesso</Text>, {
        type: 'success',
      });

      if (response.status === 201) {

        sendResponse({ wagonKey, response });

        setLoading(false);
        handleClose();
      }
    } catch (error) {
      setErrors({});
      setLoading(false);
      snackbar.show(
        <Text>
          {error?.response?.data?.message || 'Erro ao cadastrar implemento'}
        </Text>,
        {
          type: 'error',
        }
      );
    } finally {
      setLoading(false);
    }
  }

  const implementId = {
    implement: 'um',
    implement2: 'dois',
    implement3: 'tres',
  };

  useImperativeHandle(ref, () => ({
    save: handleSubmit,
  }));

  return (
    <Row className="mx-0 mt-1">
      <Col md={4} xs={12} className="mb-3">
        <Input
          label="Placa *"
          value={data.plate}
          onChange={event =>
            event.target.value.length > 7
              ? snackbar.show(<Text>Limite de dígitos para placa</Text>, {
                type: 'error',
              })
              : handleChange('plate', event.target.value.toUpperCase())
          }
          error={errors?.plate}
          id={`input-novo-implemento-${implementId[wagonKey]}-modal-placa`}
        />
      </Col>
      <Col md={4} xs={12} className="mb-3">
        <Select.Async
          label="Cidade *"
          onSearch={fetchOnlyCities}
          value={data.city}
          getOptionLabel={option =>
            `${option?.name}, ${option?.province?.uf ?? option?.uf}`
          }
          getOptionValue={option => option.id}
          onChange={value => {
            handleChange('city', value);
          }}
          error={errors?.plateCityId}
          id={`select-novo-implemento-${implementId[wagonKey]}-modal-cidade`}
        />
      </Col>
      <Col md={4} xs={12} className="mb-3">
        <Input
          label="Ano de fabricação *"
          type="number"
          value={data.yearManufacture}
          onChange={event =>
            event.target.value.length > 4
              ? snackbar.show(<Text> Limite de dígitos para Ano </Text>, {
                type: 'error',
              })
              : handleChange('yearManufacture', event.target.value)
          }
          error={errors?.yearManufacture}
          id={`input-novo-implemento-${implementId[wagonKey]}-modal-ano-fabricacao`}
        />
      </Col>
      <Col md={4} xs={12} className="mb-3">
        <Input
          label="Ano modelo *"
          type="number"
          value={data.yearModel}
          onChange={event =>
            event.target.value.length > 4
              ? snackbar.show(<Text> Limite de dígitos para Ano </Text>, {
                type: 'error',
              })
              : handleChange('yearModel', event.target.value)
          }
          error={errors?.yearModel}
          id={`input-novo-implemento-${implementId[wagonKey]}-modal-ano-modelo`}
        />
      </Col>
      <Col md={4} xs={12} className="mb-3">
        <Input
          label="Chassi *"
          value={data.chassi}
          onChange={event =>
            event.target.value.length > 20
              ? snackbar.show(
                <Text> Limite de dígitos para Chassi </Text>,
                {
                  type: 'error',
                }
              )
              : handleChange('chassi', event.target.value)
          }
          error={errors?.chassi}
          id={`input-novo-implemento-${implementId[wagonKey]}-modal-chassi`}
        />
      </Col>
      <Col md={4} xs={12} className="mb-3">
        <Select
          label="Tipo do veículo *"
          options={vehicleTypesOptions}
          getOptionLabel={option => option.name}
          getOptionValue={option => option.id}
          value={data.vehicleType}
          onChange={value => handleChange('vehicleType', value)}
          error={errors?.vehicleType}
          id={`select-novo-implemento-${implementId[wagonKey]}-modal-tipo-veiculo`}
        />
      </Col>

      <Col md={4} xs={12} className="mb-3">
        <Select
          label="Tipo de implemento *"
          options={vehicleImplementTypesOptions}
          getOptionLabel={option => option.name}
          getOptionValue={option => option.id}
          value={data.vehicleImplementType}
          onChange={value => {
            if (value?.name === 'Dolly') {
              handleChange('vehicleImplementType', value);
              handleChange('vehicleBodyType', null);
              setErrors(old => ({ ...old, vehicleBodyType: null }));
            } else {
              handleChange('vehicleImplementType', value);
            }
          }}
          error={errors?.vehicleImplementType}
          id={`select-novo-implemento-${implementId[wagonKey]}-modal-tipo-implemento`}
        />
      </Col>

      <Col md={4} xs={12} className="mb-3">
        <Select
          label="Tipo do carroceria *"
          options={vehicleBodyTypesOptions}
          getOptionLabel={option => option.name}
          getOptionValue={option => option.id}
          value={data.vehicleBodyType}
          onChange={value => handleChange('vehicleBodyType', value)}
          error={errors?.vehicleBodyType}
          isDisabled={hasSelectDollyImplementType}
          id={`select-novo-implemento-${implementId[wagonKey]}-modal-tipo-carroceria`}
        />
      </Col>
      <Col md={4} xs={12} className="mb-3">
        <Input
          label="Renavam *"
          type="number"
          value={data.renavam}
          onChange={event =>
            event.target.value.length > 11
              ? snackbar.show(
                <Text> Limite de dígitos para Renavam </Text>,
                {
                  type: 'error',
                }
              )
              : handleChange('renavam', event.target.value)
          }
          error={errors?.renavam}
          id={`input-novo-implemento-${implementId[wagonKey]}-modal-renavam`}
        />
      </Col>
      <Col md={4} xs={12} className="mb-3">
        <Select
          label="Tipo de RNTRC *"
          options={anttTypesOptions}
          getOptionLabel={option => option.name}
          getOptionValue={option => option.id}
          value={data.anttType}
          onChange={value => handleChange('anttType', value)}
          error={errors?.anttType}
          id={`select-novo-implemento-${implementId[wagonKey]}-modal-tipo-rntrc`}
        />
      </Col>
      <Col md={4} xs={12} className="mb-3">
        <Input
          label="RNTRC *"
          type="number"
          value={data.antt}
          onChange={event => handleChange('antt', event.target.value)}
          error={errors?.antt}
          id={`input-novo-implemento-${implementId[wagonKey]}-modal-tipo-rntrc`}
        />
      </Col>
      <Col md={4} xs={12} className="mb-3">
        <Input
          label="Data de emissão da RNTRC *"
          type="date"
          value={data.anttAdherence}
          onChange={event => handleChange('anttAdherence', event.target.value)}
          error={errors?.anttAdherence}
          id={`input-novo-implemento-${implementId[wagonKey]}-modal-emissao-rntrc`}
        />
      </Col>
      <Col md={4} xs={12} className="mb-3">
        <Select.Async
          label="Proprietário do documento *"
          onSearch={fetchOwners}
          onChange={value => handleChange('ownerData', value)}
          value={data.ownerData}
          getOptionLabel={option => option.label || option.socialName}
          getOptionValue={option => option.id}
          error={errors?.idOwner}
          id={`select-novo-implemento-${implementId[wagonKey]}-modal-proprietario-documento`}
        />
      </Col>
      <Col md={4} xs={12} className="mb-3">
        <Select.Async
          label="Beneficiário do veículo  *"
          onSearch={fetchOwners}
          onChange={value => handleChange('beneficiary', value)}
          value={data.beneficiary}
          getOptionLabel={option => option.label || option.socialName}
          getOptionValue={option => option.id}
          error={errors?.beneficiaryId}
        />
      </Col>
      <Col xs={12} className="mb-3">
        <BranchOfficesSelect
          value={data.branchOffices}
          onChange={value => handleChange('branchOffices', value)}
          error={errors?.branchOffices}
        />
      </Col>
    </Row>
  );
})

export default ManualRegisterWagon;
